import * as React from 'react'
import { cva } from 'class-variance-authority'

import { Slot } from '@/components/new/lib/slot'
import { cn } from '@/components/new/lib/utils'
import { BevelSurface, type BevelSurfaceColor } from '@/components/new/ui/bevelSurface'

// The icon is drawn before the label in its color; a badge rendered by its child places any icon itself
type BadgeIconProps = { asChild?: false; Icon?: React.ElementType } | { asChild: true; Icon?: never }
type BaseBadgeProps = React.ComponentPropsWithoutRef<'span'> & BadgeIconProps & { size?: 'default' | 'lg' }
type BadgeProps =
    | (BaseBadgeProps & {
          variant?: 'default' | 'solid'
          color: BevelSurfaceColor
          seed?: never
      })
    | (BaseBadgeProps & {
          variant: 'outline'
          color?: BevelSurfaceColor
          seed?: never
      })
    | (BaseBadgeProps & {
          variant: 'colored'
          color?: never
          seed: string
      })

const badgeVariants = cva(
    'group/badge inline-flex h-[22px] w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-4xl border border-transparent px-2 py-[3px] text-xs font-medium whitespace-nowrap transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3!',
    {
        variants: {
            variant: {
                // The tinted variants are painted by the bevel surface
                default: '',
                colored: '',
                solid: 'border-[var(--badge-solid-light)] bg-[var(--badge-solid-light)] text-white shadow-[0_1px_3px_rgb(0_0_0/0.2),inset_0_1px_0_rgb(255_255_255/0.26),inset_0_0_3px_1px_rgb(255_255_255/0.1),inset_0_0_0_100px_rgb(255_255_255/0.08)] dark:border-black/45 dark:bg-[var(--badge-solid-dark)] dark:shadow-[0_1px_3px_rgb(0_0_0/0.45),inset_0_1px_0_rgb(255_255_255/0.5),inset_0_-1px_0_rgb(0_0_0/0.22),inset_0_-6px_7px_-2px_rgb(0_0_0/0.34),inset_0_0_3px_1px_rgb(0_0_0/0.16),inset_0_0_0_100px_rgb(0_0_0/0.1)]',
                outline:
                    'border-border bg-background bg-linear-to-b from-transparent to-black/3 text-foreground shadow-[0_1px_2px_rgb(40_40_40/0.08)] dark:from-white/5 dark:to-transparent dark:shadow-[0_1px_2px_rgb(0_0_0/0.4),inset_0_1px_0_rgb(255_255_255/0.05)]',
            },
            size: {
                default: '',
                lg: 'h-[26px] px-2.5 py-1 text-[13px]',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    }
)

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
    (
        {
            className,
            color = 'neutral',
            seed,
            style,
            variant = 'default',
            size = 'default',
            asChild = false,
            Icon,
            children,
            ...props
        },
        ref
    ) => {
        const badgeProps = {
            ref,
            'data-slot': 'badge',
            'data-variant': variant,
            className: cn(badgeVariants({ variant, size }), className),
            ...props,
        }
        const content = asChild ? (
            children
        ) : (
            <>
                {Icon && <Icon data-icon="inline-start" />}
                {children}
            </>
        )

        if (variant === 'default' || variant === 'colored') {
            return (
                <BevelSurface
                    {...badgeProps}
                    {...(variant === 'colored' ? { seed: seed ?? '' } : { color })}
                    asChild={asChild}
                    style={style}
                >
                    {content}
                </BevelSurface>
            )
        }

        const Comp = asChild ? Slot.Root : 'span'
        const solidStyle = {
            '--badge-solid-light': color.startsWith('#') ? color : `var(--color-${color}-500)`,
            '--badge-solid-dark': color.startsWith('#') ? color : `var(--color-${color}-600)`,
            ...style,
        } as React.CSSProperties

        return (
            <Comp {...badgeProps} style={variant === 'solid' ? solidStyle : style}>
                {content}
            </Comp>
        )
    }
)

Badge.displayName = 'Badge'

export { Badge, badgeVariants }
